// ============================================
// Twin Fighter — AI Patient vs AI Nurse Arena
// Mobile-first component for automated fights
// between AI patient personas and the AI nurse.
// /twin-fighter
// ============================================

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  runSingleFight,
  runTournament,
  type FightResult,
  type FightCallbacks,
} from '../../services/patientJourney/fightService';
import {
  CUSTOMER_FUNNEL_PERSONAS,
  PERSONA_OPTIONS,
  PERSONA_MAP,
} from '../../data/personas/customerFunnelPersonas';
import type { Message, CheckResult } from '../../types/patientJourney';
import { T, F, modules } from '../quality/tokens';
import { ModuleShell } from '../quality/ModuleShell';
import { ModuleHeader } from '../quality/ModuleHeader';
import { PersonaCard } from '../quality/PersonaCard';
import { ChatBubble } from '../quality/ChatBubble';
import { ValidationCheck } from '../quality/ValidationCheck';
import { PassGauge } from '../quality/PassGauge';
import { Pill } from '../quality/Pill';

// ── Types ──

type Phase = 'setup' | 'fighting' | 'scoring' | 'results';

// ── Persona colors (stable palette for persona accent bars) ──

const PERSONA_COLORS: Record<string, string> = {
  gaslit: '#E11D48',
  skeptic: '#0D9488',
  desperate: '#7C3AED',
  biohacker: '#2563EB',
  anxious: '#D97706',
  spouse: '#16A34A',
  prepper: '#CA8A04',
  price_watcher: '#DC2626',
};

function personaColor(key: string): string {
  return PERSONA_COLORS[key] || modules.twin.color;
}

// ── Component ──

export function TwinFighter() {
  // Setup state
  const [selectedPersonaKeys, setSelectedPersonaKeys] = useState<Set<string>>(
    new Set([PERSONA_OPTIONS[0].value]),
  );
  const [phase, setPhase] = useState<Phase>('setup');

  // Fighting state
  const [conversationLog, setConversationLog] = useState<Message[]>([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [currentPersonaName, setCurrentPersonaName] = useState('');
  const [currentPersonaArchetype, setCurrentPersonaArchetype] = useState('');
  const [fightIndex, setFightIndex] = useState(0);
  const [totalFights, setTotalFights] = useState(0);

  // Scoring state
  const [scoringProgress, setScoringProgress] = useState({ current: 0, total: 0 });
  const [liveCheckResults, setLiveCheckResults] = useState<CheckResult[]>([]);

  // Results state
  const [singleResult, setSingleResult] = useState<FightResult | null>(null);
  const [tournamentResults, setTournamentResults] = useState<FightResult[]>([]);
  const [isTournament, setIsTournament] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [showCheckResults, setShowCheckResults] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const turnCount = conversationLog.length;

  // Timer
  useEffect(() => {
    if (phase !== 'fighting' && phase !== 'scoring') return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Auto-scroll during fight
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationLog.length, isWaiting]);

  // ── Persona selection handlers ──

  function togglePersona(key: string) {
    setSelectedPersonaKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow deselecting the last one
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  // ── Fight handlers ──

  const handleSingleFight = useCallback(async () => {
    const firstKey = Array.from(selectedPersonaKeys)[0];
    const persona = PERSONA_MAP[firstKey];
    if (!persona) return;

    setError(null);
    setConversationLog([]);
    setLiveCheckResults([]);
    setScoringProgress({ current: 0, total: 0 });
    setSingleResult(null);
    setTournamentResults([]);
    setIsTournament(false);
    setCurrentPersonaName(persona.name);
    setCurrentPersonaArchetype(persona.archetype);
    setFightIndex(0);
    setTotalFights(1);
    setShowCheckResults(false);
    setShowConversation(false);
    setIsWaiting(true);
    setPhase('fighting');

    const callbacks: FightCallbacks = {
      onMessage: (msg) => {
        setIsWaiting(false);
        setConversationLog((prev) => [...prev, msg]);
        // After each message, we expect the next one
        setIsWaiting(true);
      },
      onCheckScored: (result, index, total) => {
        setPhase('scoring');
        setIsWaiting(false);
        setScoringProgress({ current: index + 1, total });
        setLiveCheckResults((prev) => [...prev, result]);
      },
    };

    try {
      const result = await runSingleFight(persona, callbacks);
      setIsWaiting(false);
      setSingleResult(result);
      setPhase('results');
    } catch (err) {
      setIsWaiting(false);
      setError(err instanceof Error ? err.message : 'Fight failed');
      setPhase('results');
    }
  }, [selectedPersonaKeys]);

  const handleTournament = useCallback(async () => {
    const keys = Array.from(selectedPersonaKeys);
    const personas = keys.map((k) => PERSONA_MAP[k]).filter(Boolean);
    if (personas.length === 0) return;

    setError(null);
    setConversationLog([]);
    setLiveCheckResults([]);
    setScoringProgress({ current: 0, total: 0 });
    setSingleResult(null);
    setTournamentResults([]);
    setIsTournament(true);
    setFightIndex(0);
    setTotalFights(personas.length);
    setCurrentPersonaName(personas[0].name);
    setCurrentPersonaArchetype(personas[0].archetype);
    setShowCheckResults(false);
    setShowConversation(false);
    setIsWaiting(true);
    setPhase('fighting');

    const completedResults: FightResult[] = [];

    const callbacks: FightCallbacks = {
      onMessage: (msg) => {
        setIsWaiting(false);
        setConversationLog((prev) => [...prev, msg]);
        setIsWaiting(true);
      },
      onCheckScored: (result, index, total) => {
        setPhase('scoring');
        setIsWaiting(false);
        setScoringProgress({ current: index + 1, total });
        setLiveCheckResults((prev) => [...prev, result]);
      },
      onFightComplete: (result, index, total) => {
        completedResults.push(result);
        setTournamentResults([...completedResults]);

        // Prepare for next fight
        const nextIndex = index + 1;
        if (nextIndex < total) {
          setFightIndex(nextIndex);
          setCurrentPersonaName(personas[nextIndex].name);
          setCurrentPersonaArchetype(personas[nextIndex].archetype);
          setConversationLog([]);
          setLiveCheckResults([]);
          setScoringProgress({ current: 0, total: 0 });
          setIsWaiting(true);
          setPhase('fighting');
        }
      },
    };

    try {
      const results = await runTournament(personas, callbacks);
      setIsWaiting(false);
      setTournamentResults(results);
      setPhase('results');
    } catch (err) {
      setIsWaiting(false);
      setTournamentResults(completedResults);
      setError(err instanceof Error ? err.message : 'Tournament failed');
      setPhase('results');
    }
  }, [selectedPersonaKeys]);

  function handleReset() {
    setPhase('setup');
    setConversationLog([]);
    setLiveCheckResults([]);
    setScoringProgress({ current: 0, total: 0 });
    setSingleResult(null);
    setTournamentResults([]);
    setError(null);
    setIsWaiting(false);
    setShowCheckResults(false);
    setShowConversation(false);
    setExpandedCheckId(null);
  }

  // ── Derived data ──

  // Tournament stats
  const nurseWins = tournamentResults.filter((r) => r.outcome === 'nurse_won').length;
  const personaWins = tournamentResults.filter((r) => r.outcome === 'persona_won').length;
  const avgPassRate = tournamentResults.length > 0
    ? Math.round(tournamentResults.reduce((sum, r) => sum + r.passRate, 0) / tournamentResults.length)
    : 0;

  // Sort tournament results by pass rate ascending (hardest persona at top)
  const sortedTournamentResults = [...tournamentResults].sort((a, b) => a.passRate - b.passRate);

  // Get check item text from persona
  function getCheckText(checkId: string): string {
    for (const persona of CUSTOMER_FUNNEL_PERSONAS) {
      const check = persona.validationChecklist.find((c) => c.checkId === checkId);
      if (check) return check.check;
    }
    return checkId;
  }

  // Get check category from persona
  function getCheckCategory(checkId: string): string {
    for (const persona of CUSTOMER_FUNNEL_PERSONAS) {
      const check = persona.validationChecklist.find((c) => c.checkId === checkId);
      if (check) return check.category;
    }
    return 'Clinical';
  }

  const themeColor = modules.twin.color; // #DC2626

  // ── Render ──

  return (
    <ModuleShell>
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        background: T.bg,
        fontFamily: F.body,
      }}>
        {/* ── Header ── */}
        <div style={{ flexShrink: 0 }}>
          <ModuleHeader
            module="twin"
            subtitle={
              phase === 'setup' ? 'Automated AI-vs-AI Quality Testing'
              : phase === 'fighting' ? `${elapsed}s \u2014 ${currentPersonaName} fighting`
              : phase === 'scoring' ? `Scoring ${scoringProgress.current}/${scoringProgress.total}`
              : isTournament ? `${nurseWins}W-${personaWins}L`
              : singleResult ? `${singleResult.passRate}%` : 'Done'
            }
          />
        </div>

        {/* ── Setup Phase ── */}
        {phase === 'setup' && (
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '24px 16px',
          }}>
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              {/* Title section */}
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{
                  fontSize: T.fontSize.xl, fontWeight: 700,
                  color: T.text, fontFamily: F.body,
                }}>
                  AI Arena
                </div>
                <div style={{
                  fontSize: T.fontSize.sm, color: T.textMuted,
                  marginTop: 4, fontFamily: F.body,
                }}>
                  AI patient personas fight the AI nurse. Pick your fighters.
                </div>
                <div style={{
                  fontSize: T.fontSize.xs, color: themeColor,
                  marginTop: 8, fontFamily: F.body, opacity: 0.8,
                }}>
                  Select multiple for tournament mode.
                </div>
              </div>

              {/* Mode toggle pill */}
              <div style={{
                display: 'flex', justifyContent: 'center', marginBottom: 20,
              }}>
                <div style={{
                  display: 'inline-flex', gap: 4,
                  background: T.bgSoft, borderRadius: T.radius.full,
                  padding: 4,
                }}>
                  <div style={{
                    padding: '6px 14px', borderRadius: T.radius.full,
                    fontSize: T.fontSize.xs, fontWeight: 600,
                    fontFamily: F.mono,
                    background: selectedPersonaKeys.size <= 1 ? T.bgCard : 'transparent',
                    color: selectedPersonaKeys.size <= 1 ? T.text : T.textMuted,
                    boxShadow: selectedPersonaKeys.size <= 1 ? T.shadowSm : 'none',
                  }}>
                    Single
                  </div>
                  <div style={{
                    padding: '6px 14px', borderRadius: T.radius.full,
                    fontSize: T.fontSize.xs, fontWeight: 600,
                    fontFamily: F.mono,
                    background: selectedPersonaKeys.size > 1 ? T.bgCard : 'transparent',
                    color: selectedPersonaKeys.size > 1 ? T.text : T.textMuted,
                    boxShadow: selectedPersonaKeys.size > 1 ? T.shadowSm : 'none',
                  }}>
                    Tournament
                  </div>
                </div>
              </div>

              {/* Persona cards */}
              <div style={{ marginBottom: 24 }}>
                {PERSONA_OPTIONS.map((opt) => {
                  const p = PERSONA_MAP[opt.value];
                  const isSelected = selectedPersonaKeys.has(opt.value);
                  const color = personaColor(opt.value);
                  return (
                    <div key={opt.value}>
                      <PersonaCard
                        name={opt.label}
                        archetype={opt.sub}
                        color={color}
                        selected={isSelected}
                        onSelect={() => togglePersona(opt.value)}
                      />
                      {isSelected && p && (
                        <div style={{
                          marginTop: -4, marginBottom: 12,
                          marginLeft: 20, padding: '8px 14px',
                          background: T.bgSoft,
                          borderRadius: `0 0 ${T.radius.md}px ${T.radius.md}px`,
                          borderLeft: `3px solid ${color}`,
                        }}>
                          <div style={{
                            fontSize: T.fontSize.xs, color: T.textSoft,
                            fontFamily: F.body, lineHeight: 1.5,
                          }}>
                            {p.conditions.join(' / ')}
                          </div>
                          {p.medications.length > 0 && (
                            <div style={{
                              fontSize: T.fontSize.xs, color: T.warning,
                              fontFamily: F.body, marginTop: 2,
                            }}>
                              Meds: {p.medications.map((m) => m.name).join(', ')}
                            </div>
                          )}
                          <div style={{
                            fontSize: T.fontSize.xs, color: T.textGhost,
                            fontFamily: F.mono, marginTop: 4,
                          }}>
                            {p.labs.filter((l) => l.trigger).length} markers
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  onClick={handleSingleFight}
                  disabled={selectedPersonaKeys.size === 0}
                  style={{
                    width: '100%', padding: '16px 0',
                    borderRadius: T.radius.lg, border: 'none',
                    background: `linear-gradient(135deg, ${themeColor}, #B91C1C)`,
                    color: '#fff', fontWeight: 700,
                    fontSize: T.fontSize.md, fontFamily: F.body,
                    cursor: selectedPersonaKeys.size === 0 ? 'not-allowed' : 'pointer',
                    opacity: selectedPersonaKeys.size === 0 ? 0.4 : 1,
                    boxShadow: T.shadowAccent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'opacity 0.2s',
                  }}
                >
                  Run Single Fight
                </button>

                {selectedPersonaKeys.size > 1 && (
                  <button
                    onClick={handleTournament}
                    style={{
                      width: '100%', padding: '16px 0',
                      borderRadius: T.radius.lg,
                      border: `1.5px solid ${themeColor}`,
                      background: T.dangerSoft,
                      color: themeColor, fontWeight: 700,
                      fontSize: T.fontSize.md, fontFamily: F.body,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      transition: 'all 0.2s',
                    }}
                  >
                    Run Tournament ({selectedPersonaKeys.size} fighters)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Fighting Phase ── */}
        {(phase === 'fighting' || phase === 'scoring') && (
          <>
            {/* VS header bar */}
            <div style={{
              flexShrink: 0, padding: '10px 16px',
              background: T.bgCard,
              borderBottom: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: T.fontSize.sm, fontWeight: 600,
                  color: T.gold, fontFamily: F.body,
                }}>
                  Nurse
                </span>
              </div>
              <Pill text="VS" color="#fff" bg={themeColor} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: T.fontSize.sm, fontWeight: 600,
                  color: T.text, fontFamily: F.body,
                }}>
                  {currentPersonaName}
                </span>
                <span style={{
                  fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body,
                }}>
                  ({currentPersonaArchetype})
                </span>
              </div>
            </div>

            {/* Live score ticker */}
            <div style={{
              flexShrink: 0, padding: '6px 16px',
              background: T.bgSoft,
              borderBottom: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {phase === 'fighting' && (
                <>
                  <Pill
                    text={`Turn ${turnCount}`}
                    color={themeColor}
                    bg={T.dangerSoft}
                  />
                  <Loader2 size={12} style={{ color: themeColor, animation: 'spin 1s linear infinite' }} />
                </>
              )}
              {phase === 'scoring' && liveCheckResults.length > 0 && (
                <>
                  {liveCheckResults.map((cr) => (
                    <Pill
                      key={cr.checkId}
                      text={cr.pass ? '\u2713' : '\u2717'}
                      color={cr.pass ? T.success : T.danger}
                      bg={cr.pass ? T.successSoft : T.dangerSoft}
                    />
                  ))}
                </>
              )}
              <div style={{ flex: 1 }} />
              <span style={{
                fontSize: T.fontSize.xs, color: T.textGhost,
                fontFamily: F.mono,
              }}>
                {elapsed}s
              </span>
            </div>

            {/* Tournament progress bar */}
            {isTournament && totalFights > 1 && (
              <div style={{
                flexShrink: 0, padding: '8px 16px',
                background: T.dangerSoft,
                borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{
                  fontSize: T.fontSize.xs, color: themeColor,
                  fontWeight: 600, fontFamily: F.mono, flexShrink: 0,
                }}>
                  Fight {fightIndex + 1}/{totalFights}
                </span>
                <div style={{
                  flex: 1, background: T.border,
                  borderRadius: T.radius.full, height: 6, overflow: 'hidden',
                }}>
                  <div style={{
                    height: 6, borderRadius: T.radius.full,
                    background: themeColor,
                    transition: 'width 0.5s ease',
                    width: `${((fightIndex + (phase === 'scoring' ? 0.5 : 0)) / totalFights) * 100}%`,
                  }} />
                </div>
              </div>
            )}

            {/* Scoring progress */}
            {phase === 'scoring' && (
              <div style={{
                flexShrink: 0, padding: '8px 16px',
                background: T.dangerSoft,
                borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{
                  fontSize: T.fontSize.xs, color: themeColor,
                  fontWeight: 600, fontFamily: F.mono,
                }}>
                  Scoring
                </span>
                <div style={{
                  flex: 1, background: T.border,
                  borderRadius: T.radius.full, height: 6, overflow: 'hidden',
                }}>
                  <div style={{
                    height: 6, borderRadius: T.radius.full,
                    background: themeColor,
                    transition: 'width 0.3s ease',
                    width: `${scoringProgress.total > 0 ? (scoringProgress.current / scoringProgress.total) * 100 : 0}%`,
                  }} />
                </div>
                <span style={{
                  fontSize: T.fontSize.xs, color: themeColor,
                  fontFamily: F.mono, fontWeight: 600,
                }}>
                  {scoringProgress.current}/{scoringProgress.total}
                </span>
              </div>
            )}

            {/* Live message stream */}
            <div style={{
              flex: 1, overflowY: 'auto',
              padding: 16,
            }}>
              {/* Scoring results stream (during scoring phase) */}
              {phase === 'scoring' && liveCheckResults.length > 0 && (
                <div style={{
                  marginBottom: 16, paddingBottom: 16,
                  borderBottom: `1px solid ${T.border}`,
                }}>
                  {liveCheckResults.map((cr) => (
                    <ValidationCheck
                      key={cr.checkId}
                      name={getCheckText(cr.checkId)}
                      category={getCheckCategory(cr.checkId)}
                      passed={cr.pass === true}
                      reasoning={cr.notes || undefined}
                      expanded={expandedCheckId === cr.checkId}
                      onToggle={() => setExpandedCheckId(expandedCheckId === cr.checkId ? null : cr.checkId)}
                    />
                  ))}
                </div>
              )}

              {/* Conversation messages */}
              {conversationLog.map((msg, idx) => {
                const isNurse = msg.role === 'ai_nurse';
                return (
                  <ChatBubble
                    key={idx}
                    text={msg.content}
                    isNurse={isNurse}
                    themeColor={themeColor}
                    label={isNurse ? 'Nurse' : currentPersonaName}
                  />
                );
              })}

              {/* Typing indicator */}
              {isWaiting && phase === 'fighting' && (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'flex-start', marginBottom: 12,
                }}>
                  <div style={{
                    padding: '10px 14px',
                    background: T.bgInput,
                    borderRadius: '14px 14px 14px 4px',
                    boxShadow: T.shadowSm,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {[0, 150, 300].map((delay) => (
                      <div key={delay} style={{
                        width: 8, height: 8,
                        borderRadius: T.radius.full,
                        background: T.textGhost,
                        animation: 'bounce 1.2s infinite',
                        animationDelay: `${delay}ms`,
                      }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state at start */}
              {phase === 'fighting' && conversationLog.length === 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '48px 0',
                }}>
                  <Loader2
                    size={32}
                    style={{ color: themeColor, marginBottom: 12, animation: 'spin 1s linear infinite' }}
                  />
                  <div style={{
                    fontSize: T.fontSize.sm, color: T.textMuted,
                    fontFamily: F.body,
                  }}>
                    Starting fight...
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          </>
        )}

        {/* ── Results Phase (single fight) ── */}
        {phase === 'results' && !isTournament && singleResult && (
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '24px 16px',
          }}>
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              {/* Outcome: PassGauge + name + summary + pill */}
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', marginBottom: 24,
              }}>
                <PassGauge score={singleResult.passRate} size={110} />
                <div style={{
                  fontSize: T.fontSize.md, fontWeight: 700,
                  color: T.text, marginTop: 12, fontFamily: F.body,
                }}>
                  vs {singleResult.personaName}
                </div>
                <div style={{
                  fontSize: T.fontSize.sm, color: T.textMuted,
                  marginTop: 4, fontFamily: F.body,
                }}>
                  {singleResult.checkResults.filter((c) => c.pass === true).length}/{singleResult.checkResults.length} checks passed
                </div>
                <div style={{ marginTop: 10 }}>
                  <Pill
                    text={singleResult.outcome === 'nurse_won' ? 'NURSE WINS' : 'NURSE LOSES'}
                    color={singleResult.outcome === 'nurse_won' ? T.success : T.danger}
                    bg={singleResult.outcome === 'nurse_won' ? T.successSoft : T.dangerSoft}
                  />
                </div>
                <div style={{
                  fontSize: T.fontSize.xs, color: T.textGhost,
                  marginTop: 8, fontFamily: F.mono,
                }}>
                  {singleResult.conversationLog.length} messages in {(singleResult.duration / 1000).toFixed(0)}s
                </div>
              </div>

              {/* Check Results Accordion */}
              <div style={{
                borderRadius: T.radius.lg, overflow: 'hidden',
                border: `1px solid ${T.border}`,
                marginBottom: 12,
              }}>
                <button
                  onClick={() => setShowCheckResults(!showCheckResults)}
                  style={{
                    width: '100%', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', background: T.bgCard,
                    border: 'none', cursor: 'pointer',
                    fontFamily: F.body,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={14} style={{ color: themeColor }} />
                    <span style={{
                      fontSize: T.fontSize.sm, fontWeight: 600, color: T.text,
                    }}>
                      Check Results
                    </span>
                    <span style={{
                      fontSize: T.fontSize.xs, color: T.textMuted,
                    }}>
                      ({singleResult.checkResults.filter((c) => c.pass === true).length}/{singleResult.checkResults.length} passed)
                    </span>
                  </div>
                  {showCheckResults
                    ? <ChevronUp size={16} style={{ color: T.textMuted }} />
                    : <ChevronDown size={16} style={{ color: T.textMuted }} />
                  }
                </button>

                {showCheckResults && (
                  <div style={{
                    padding: 12, background: T.bgSoft,
                    borderTop: `1px solid ${T.border}`,
                  }}>
                    {singleResult.checkResults.map((cr) => (
                      <ValidationCheck
                        key={cr.checkId}
                        name={getCheckText(cr.checkId)}
                        category={getCheckCategory(cr.checkId)}
                        passed={cr.pass === true}
                        reasoning={cr.notes || undefined}
                        expanded={expandedCheckId === cr.checkId}
                        onToggle={() => setExpandedCheckId(expandedCheckId === cr.checkId ? null : cr.checkId)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Full Conversation Accordion */}
              <div style={{
                borderRadius: T.radius.lg, overflow: 'hidden',
                border: `1px solid ${T.border}`,
                marginBottom: 20,
              }}>
                <button
                  onClick={() => setShowConversation(!showConversation)}
                  style={{
                    width: '100%', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', background: T.bgCard,
                    border: 'none', cursor: 'pointer',
                    fontFamily: F.body,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: T.fontSize.sm, fontWeight: 600, color: T.text,
                    }}>
                      Full Conversation
                    </span>
                    <span style={{
                      fontSize: T.fontSize.xs, color: T.textMuted,
                    }}>
                      ({singleResult.conversationLog.length} messages)
                    </span>
                  </div>
                  {showConversation
                    ? <ChevronUp size={16} style={{ color: T.textMuted }} />
                    : <ChevronDown size={16} style={{ color: T.textMuted }} />
                  }
                </button>

                {showConversation && (
                  <div style={{
                    padding: 12, background: T.bgSoft,
                    borderTop: `1px solid ${T.border}`,
                    maxHeight: '50vh', overflowY: 'auto',
                  }}>
                    {singleResult.conversationLog.map((msg, idx) => {
                      const isNurse = msg.role === 'ai_nurse';
                      return (
                        <ChatBubble
                          key={idx}
                          text={msg.content}
                          isNurse={isNurse}
                          themeColor={themeColor}
                          label={isNurse ? 'Nurse' : singleResult.personaName}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* New Fight button */}
              <button
                onClick={handleReset}
                style={{
                  width: '100%', padding: '16px 0',
                  borderRadius: T.radius.lg, border: 'none',
                  background: `linear-gradient(135deg, ${themeColor}, #B91C1C)`,
                  color: '#fff', fontWeight: 700,
                  fontSize: T.fontSize.md, fontFamily: F.body,
                  cursor: 'pointer',
                  boxShadow: T.shadowAccent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <RotateCcw size={20} />
                New Fight
              </button>
            </div>
          </div>
        )}

        {/* ── Tournament Results Phase ── */}
        {phase === 'results' && isTournament && tournamentResults.length > 0 && (
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '24px 16px',
          }}>
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              {/* Aggregate PassGauge + stats */}
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', marginBottom: 24,
              }}>
                <PassGauge score={avgPassRate} size={110} />
                <div style={{
                  fontSize: T.fontSize.md, fontWeight: 700,
                  color: T.text, marginTop: 12, fontFamily: F.body,
                }}>
                  Nurse: {nurseWins}W-{personaWins}L
                </div>
                <div style={{
                  fontSize: T.fontSize.sm, color: T.textMuted,
                  marginTop: 4, fontFamily: F.body,
                }}>
                  Avg {avgPassRate}% across {tournamentResults.length} fights
                </div>
              </div>

              {/* Leaderboard: sorted persona cards with scores */}
              <div style={{
                borderRadius: T.radius.lg, overflow: 'hidden',
                border: `1px solid ${T.border}`,
                marginBottom: 20,
              }}>
                {/* Table header */}
                <div style={{
                  display: 'flex', alignItems: 'center',
                  padding: '10px 16px',
                  background: T.bgSoft,
                  borderBottom: `1px solid ${T.border}`,
                  fontSize: T.fontSize.xs, fontWeight: 600,
                  color: T.textMuted, fontFamily: F.mono,
                }}>
                  <span style={{ flex: 1 }}>Persona</span>
                  <span style={{ width: 100, textAlign: 'center' }}>Archetype</span>
                  <span style={{ width: 80, textAlign: 'center' }}>Outcome</span>
                  <span style={{ width: 56, textAlign: 'right' }}>Rate</span>
                </div>

                {/* Table rows */}
                {sortedTournamentResults.map((result) => (
                  <div
                    key={result.fightId}
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '12px 16px',
                      background: result.outcome === 'nurse_won' ? T.successSoft : T.dangerSoft,
                      borderBottom: `1px solid ${T.border}`,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: T.fontSize.sm, fontWeight: 600,
                        color: T.text, fontFamily: F.body,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {result.personaName}
                      </div>
                    </div>
                    <div style={{ width: 100, textAlign: 'center' }}>
                      <span style={{
                        fontSize: T.fontSize.xs, color: T.textMuted,
                        fontFamily: F.body,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        display: 'block',
                      }}>
                        {result.personaArchetype}
                      </span>
                    </div>
                    <div style={{ width: 80, textAlign: 'center' }}>
                      <Pill
                        text={result.outcome === 'nurse_won' ? 'Nurse' : 'Persona'}
                        color={result.outcome === 'nurse_won' ? T.success : T.danger}
                        bg={result.outcome === 'nurse_won' ? T.successSoft : T.dangerSoft}
                      />
                    </div>
                    <div style={{ width: 56, textAlign: 'right' }}>
                      <span style={{
                        fontSize: T.fontSize.sm, fontWeight: 700,
                        fontFamily: F.mono,
                        color: result.passRate >= 90 ? T.success
                          : result.passRate >= 70 ? T.warning
                          : T.danger,
                      }}>
                        {result.passRate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Run Again button */}
              <button
                onClick={handleReset}
                style={{
                  width: '100%', padding: '16px 0',
                  borderRadius: T.radius.lg, border: 'none',
                  background: `linear-gradient(135deg, ${themeColor}, #B91C1C)`,
                  color: '#fff', fontWeight: 700,
                  fontSize: T.fontSize.md, fontFamily: F.body,
                  cursor: 'pointer',
                  boxShadow: T.shadowAccent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <RotateCcw size={20} />
                Run Again
              </button>
            </div>
          </div>
        )}

        {/* ── Results fallback: no results but in results phase ── */}
        {phase === 'results' && !singleResult && tournamentResults.length === 0 && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '0 16px',
          }}>
            <XCircle size={48} style={{ color: T.danger, marginBottom: 12 }} />
            <div style={{
              fontSize: T.fontSize.lg, fontWeight: 600,
              color: T.text, fontFamily: F.body,
            }}>
              Fight failed
            </div>
            <div style={{
              fontSize: T.fontSize.sm, color: T.textMuted,
              marginTop: 4, fontFamily: F.body,
            }}>
              {error || 'Unknown error occurred'}
            </div>
            <button
              onClick={handleReset}
              style={{
                marginTop: 24, padding: '12px 24px',
                borderRadius: T.radius.lg, border: 'none',
                background: `linear-gradient(135deg, ${themeColor}, #B91C1C)`,
                color: '#fff', fontWeight: 700,
                fontSize: T.fontSize.base, fontFamily: F.body,
                cursor: 'pointer',
                boxShadow: T.shadowAccent,
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* ── Error toast ── */}
        {error && phase !== 'results' && (
          <div style={{
            position: 'fixed', bottom: 80, left: 16, right: 16,
            background: T.danger, color: '#fff',
            borderRadius: T.radius.lg,
            padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 8,
            zIndex: 50,
            boxShadow: T.shadowLg,
          }}>
            <XCircle size={16} style={{ flexShrink: 0 }} />
            <span style={{
              fontSize: T.fontSize.sm, flex: 1, fontFamily: F.body,
            }}>
              {error}
            </span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.7)',
                fontSize: T.fontSize.xs, fontWeight: 600,
                cursor: 'pointer', fontFamily: F.body,
              }}
            >
              DISMISS
            </button>
          </div>
        )}
      </div>

      {/* Keyframe animations injected via style tag */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </ModuleShell>
  );
}
